import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Chip, Link as MuLink } from '@mui/material';

import { routes } from '../../../../../constants';
import { compressClassYearArray } from '../../../../../utils/compressClassYearArray';
import useRouter from '../../../../../utils/useRouter';
import { AddMenuButton, mapContentTypeName, Metadata, MuiCardBaseProps } from '../MuiCardBase';
import CardContainer from './MuiCardBaseShortContainer';
import CardCover from '../MuiCardBaseCover';
import CardMainContent from './MuiCardBaseShortMainContent';
import CardContentWrapper from './MuiCardBaseShortContentWrapper';
import CardMetadata from './MuiCardBaseShortMetadata';
import ShowLessButton from './MuiCardBaseShortShowLessButton';
import CardDescription from './MuiCardBaseShortDescription';
import CardDetails from './MuiCardBaseShortDetails';
import CardHeaderWithTitle from './MuiCardBaseShortHeaderWithTitle';

export const MuiCardBaseShort: React.FC<MuiCardBaseProps> = (props) => {
  const {
    id,
    index,
    contentType,
    publisher,
    cover,
    isReadOnly,
    title,
    description,
    subjects,
    classYears,
    link,
    linkFunction,
    date,
    chipText,
    isPinned,
    isNew,
    itemData,
  } = props;

  const { t } = useTranslation();
  const { router } = useRouter();
  const [isTruncated, setIsTruncated] = useState(true);
  const toggleTruncated = () => setIsTruncated(!isTruncated);

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    to: string
  ) => {
    e.stopPropagation();
    linkFunction ? linkFunction() : router.push(to);
  };

  const itemLink: string =
    contentType === 'product'
      ? `/${routes.productPage.root}/${id}`
      : `/${routes.reader.root}/${id}`;

  return (
    <CardContainer contentType={contentType} isNew={isNew} isTruncated={isTruncated}>
      <AddMenuButton
        isPinned={isPinned}
        type={contentType}
        id={id}
        itemData={itemData}
        index={index}
      />
      <CardMainContent>
        <CardCover
          cover={cover}
          itemLink={itemLink}
          isWide={contentType === 'video'}
          withPlayIcon={contentType === 'video'}
        />
        <CardContentWrapper isTruncated={isTruncated}>
          <CardHeaderWithTitle
            header={t(mapContentTypeName(contentType))}
            title={title}
            caption={publisher}
            itemLink={itemLink}
            isTruncated={isTruncated}
          />
          <CardMetadata toggleTruncated={toggleTruncated} isTruncated={isTruncated}>
            {chipText && <span>{chipText}</span>}
            {classYears && classYears.length > 0 && (
              <>
                <span>•</span>
                <span>
                  { `${compressClassYearArray(classYears).join(', ')} ${ t('Class year')} `}
                </span>
              </>
            )}
          </CardMetadata>
        </CardContentWrapper>
      </CardMainContent>

      {!isTruncated && 
        <>
          <ShowLessButton toggleTruncated={toggleTruncated} />
          {isReadOnly && (
            <Box>
              <Chip label={t('Read only')} size='small' />
            </Box>
          )}
          <CardDescription description={description} truncateDescriptionAt={3} />
          <CardDetails>
            {link && (
              <MuLink
                underline='none'
                onClick={(e) => handleLinkClick(e, link.to)}
                data-testid='mui-card-link'
              >
                <Metadata
                  icon={link.icon}
                  label={link.label}
                  sx={{ color: undefined, stroke: undefined }}
                />
              </MuLink>
            )}
            {subjects && <Metadata icon='tag' label={subjects.join(', ')} />}
            {date && <Metadata icon='calendar' label={date} />}
          </CardDetails>
        </>
      }
    </CardContainer>
  );
};

export default MuiCardBaseShort;
